/**
 * previewSources.ts — what the Edit Resume live-preview panel is showing.
 *
 * The panel always draws the SAME schematic; the selector only swaps where the
 * CONTENT comes from:
 *
 *   • "Live editor preview" — the unsaved draft in the editor. This is also the
 *     GENERIC master resume: that page is published from this very resumejson,
 *     so a separate "generic master" option would render pixel-for-pixel the
 *     same thing minus unsaved edits. It was collapsed into the live row
 *     (owner design 2026-08-16) rather than listed twice.
 *   • one row per ROLE-TARGETED resume ("DM") — its content is FROZEN from that
 *     resume's last generation (lengths matter, wording does not) while the
 *     structure keeps following this editor.
 *
 * There is no iframe any more: the published page is reachable from the "open
 * published page" link beside the selector, not framed inside the panel.
 *
 * Option ids are derived from the PATH, not from a record id, so the selection
 * survives a regenerate (which mints a fresh record for the same URL).
 */
import { masterResumeUrl, normalizeMasterPath } from './masterUrls';
import type { DedicatedMaster, DedicatedMasterState } from '../types';

/** The in-editor schematic preview — always available, always the fallback. */
export const PREVIEW_SOURCE_LIVE = 'live';

/** States in which a role-targeted resume has generated content to preview. */
const DM_PUBLISHED_STATES = new Set<DedicatedMasterState>(['ready']);

export interface PreviewSource {
  /** Stable id used by the <select> and by localStorage. */
  value: string;
  /** Text shown in the selector, suffix included. */
  label: string;
  /** Master path this option previews (never ''). */
  path: string;
  /** True while the page has not been generated yet (or failed). */
  pending: boolean;
  /** Record id — the handle the editor invalidates cached content by. */
  dmId: string;
  /** Synthetic job the frozen content is fetched with. */
  jobid: string;
}

export interface PreviewSourceInput {
  dedicatedMasters?: DedicatedMaster[] | null;
}

/** Suffix that tells the user why an option may not show current content. */
function dmSuffix(state: DedicatedMasterState): string {
  if (DM_PUBLISHED_STATES.has(state)) return '';
  return state === 'error' ? ' (not published)' : ' (generating…)';
}

/**
 * Every role-targeted resume this panel can preview, in stored order. The
 * "Live editor preview" row is NOT included — it is rendered by the panel
 * itself, always sits first, and doubles as the generic master preview.
 */
export function buildPreviewSources(input: PreviewSourceInput): PreviewSource[] {
  const out: PreviewSource[] = [];
  for (const dm of input.dedicatedMasters || []) {
    const path = normalizeMasterPath(dm?.path);
    if (!path) continue;
    out.push({
      value: `dm:${path}`,
      label: `${path}${dmSuffix(dm.state)}`,
      path,
      pending: !DM_PUBLISHED_STATES.has(dm.state),
      dmId: dm.id || '',
      jobid: dm.jobid || '',
    });
  }
  return out;
}

/** localStorage key for the panel's selection (per user AND per resume). */
export function previewSourceKey(
  username: string | null | undefined,
  resumename: string | null | undefined,
): string {
  return `ss_edit_preview_source_${(username || '_').toLowerCase()}_${resumename || '__new__'}`;
}

/**
 * Resolve a stored id against the CURRENT options. Anything unknown — a deleted
 * role-targeted resume, a renamed path, junk — falls back to the live preview
 * rather than previewing a resume that no longer exists.
 */
export function resolvePreviewSource(stored: unknown, sources: PreviewSource[]): string {
  if (typeof stored !== 'string' || !stored) return PREVIEW_SOURCE_LIVE;
  if (stored === PREVIEW_SOURCE_LIVE) return PREVIEW_SOURCE_LIVE;
  return sources.some(s => s.value === stored) ? stored : PREVIEW_SOURCE_LIVE;
}

/** The option matching an id, or null for the live preview / an unknown id. */
export function findPreviewSource(value: string, sources: PreviewSource[]): PreviewSource | null {
  return sources.find(s => s.value === value) || null;
}

/**
 * The published page behind the CURRENT selection — a role-targeted resume's
 * own path, or (for the live/generic row) the naked root URL, but ONLY when
 * this resume is the one published there. '' means there is nothing to open,
 * and the panel hides the link rather than pointing at someone else's page.
 */
export function publishedPageUrl(
  username: string | null | undefined,
  source: PreviewSource | null,
  generic: { publishMaster?: boolean },
): string {
  if (!username) return '';
  if (source) return source.pending ? '' : masterResumeUrl(username, source.path);
  return generic.publishMaster ? masterResumeUrl(username, '') : '';
}
